import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  Divider,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from '@mui/material';
import { getPreviewPlan } from '@services/runApi.service';
import type { PlanPreviewData, PreviewStep, ApproverRef } from '@/types/run-stage.types';

interface PreviewPlanStepsProps {
  runId: string;
  runUid?: string;
}

export const PreviewPlanSteps: React.FC<PreviewPlanStepsProps> = ({ runId, runUid }) => {
  const [preview, setPreview] = useState<PlanPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getPreviewPlan(runId, runUid)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [runId, runUid]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  const steps = preview?.translation ?? [];

  if (!steps.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary" variant="body2">No steps to preview.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <List disablePadding>
        {steps.map((step, idx) => (
          <React.Fragment key={`${step.stepId}-${step.cloneNumber ?? 0}-${idx}`}>
            {idx > 0 && <Divider />}
            <StepPreviewItem step={step} />
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

const StepPreviewItem: React.FC<{ step: PreviewStep }> = ({ step }) => {
  return (
    <ListItem sx={{ display: 'block', py: 1.5, px: 1 }}>
      {/* Step number + text */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          {step.formattedStepNumber || '—'}
        </Typography>
        {step.sanitizedTranslation ? (
          <Typography
            variant="body2"
            dangerouslySetInnerHTML={{ __html: step.sanitizedTranslation }}
            sx={{ flex: 1, '& p': { margin: 0 } }}
          />
        ) : (
          <Typography variant="body2" sx={{ flex: 1 }}>
            {step.formattedStepText || ''}
          </Typography>
        )}
      </Box>

      {/* Mapped Materials */}
      {step.mappedMaterials && step.mappedMaterials.length > 0 && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Materials:
          </Typography>
          {step.mappedMaterials.map((m, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', ml: 1 }}>
              {m.displayMappedMaterial || '—'}
            </Typography>
          ))}
        </Box>
      )}

      {/* Mapped Instrument */}
      {step.mappedInstrument && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Instrument:
          </Typography>
          <Typography variant="caption" sx={{ ml: 1 }}>{step.mappedInstrument}</Typography>
        </Box>
      )}

      {/* Recommended Instruments */}
      {step.recommendedInstruments && step.recommendedInstruments.length > 0 && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Recommended:
          </Typography>
          {step.recommendedInstruments.map((r, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', ml: 1 }}>
              {r.label}
            </Typography>
          ))}
        </Box>
      )}

      {/* Checklists */}
      {step.checklists && step.checklists.length > 0 && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          {step.checklists.map((cl, i) => (
            <Box key={i} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {cl.title} {cl.mandatory && <Chip label="Mandatory" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} />}
              </Typography>
              {cl.items?.map((item, j) => (
                <Typography key={j} variant="caption" sx={{ display: 'block', ml: 1 }}>
                  {j + 1}. {item}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      )}

      {/* Approvers */}
      {step.approvers && step.approvers.length > 0 && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
            Approvers:
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 400 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Approver</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Status</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Approved At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {step.approvers.map((a: ApproverRef, i: number) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{a.user?.name || a.user?.email || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>
                      <Chip
                        label={a.status || 'PENDING'}
                        size="small"
                        color={a.status === 'APPROVED' ? 'success' : 'default'}
                        sx={{ height: 18, fontSize: '0.6rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>
                      {a.approvedAt ? new Date(a.approvedAt).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Notifications */}
      {step.notifications && step.notifications.length > 0 && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Notifications:
          </Typography>
          {step.notifications.map((n, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', ml: 1 }}>
              {n.name} ({n.type || 'After Step'})
            </Typography>
          ))}
        </Box>
      )}

      {/* Notes */}
      {step.notes && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Notes:</Typography>
          <Typography variant="caption" sx={{ ml: 1 }}>{step.notes}</Typography>
        </Box>
      )}

      {/* Comments */}
      {step.comments && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Comments:</Typography>
          <Typography variant="caption" sx={{ ml: 1 }}>{step.comments}</Typography>
        </Box>
      )}
    </ListItem>
  );
};
